/**
 * Axios Configuration
 * Configures axios instance with interceptors for authentication
 */

import axios from 'axios';
import { tokenStorage } from '../utils/tokenStorage';
import { authService } from '../services/authService';
import qs from "qs";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const API_KEY = 'smartplan-access-key-12345';

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  paramsSerializer: params => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => {
        if (v === null || v === undefined || v === "") return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
      })
    );

    return qs.stringify(cleanParams, { arrayFormat: "repeat" });
  },
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
  },
});

// Request interceptor - Add Bearer token to all requests
api.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getAccessToken();
    
    // Always add X-API-Key header
    if (config.headers) {
      config.headers['X-API-Key'] = API_KEY;
    }

    // Add Bearer token if available
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh and 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the access token
        await authService.refreshAccessToken();

        // Retry the original request with new token
        const token = tokenStorage.getAccessToken();
        if (token && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user and redirect to login
        authService.logout();
        
        // Only redirect if we're not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
